import { apiClient } from "./apiClient";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type EnterpriseRole = "enterprise_admin" | "enterprise_reviewer";

export interface EnterprisePublicView {
  id: string;
  slug: string;
  name: string;
  subdomain: string;
  logoUrl: string | null;
  logoUrlDark: string | null;
  primaryColor: string | null;
  primaryColorDark: string | null;
}

export interface EnterpriseMembershipView {
  membershipId: string;
  role: EnterpriseRole;
  enterprise: EnterprisePublicView & { isActive: boolean };
}

export interface EnterpriseFullView extends EnterprisePublicView {
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseMemberUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export interface EnterpriseMember {
  id: string;
  userId: string;
  role: EnterpriseRole;
  user: EnterpriseMemberUser;
  acceptedAt: string | null;
  invitedAt: string | null;
}

export interface EnterpriseInvite {
  id: string;
  enterpriseId: string;
  email: string;
  role: EnterpriseRole;
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
}

export interface UpdateEnterprisePayload {
  name?: string;
  logoUrl?: string | null;
  logoUrlDark?: string | null;
  primaryColor?: string | null;
}

export interface InviteMemberPayload {
  email: string;
  role: EnterpriseRole;
}

// ---------------------------------------------------------------------------
// Public (no auth)
// ---------------------------------------------------------------------------

export async function fetchEnterpriseBySubdomain(
  slug: string,
): Promise<EnterprisePublicView> {
  const res = await apiClient.get<EnterprisePublicView>(
    `/enterprises/by-subdomain/${encodeURIComponent(slug)}`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// My memberships (auth, no subdomain header needed)
// ---------------------------------------------------------------------------

export async function fetchMyEnterpriseMemberships(): Promise<
  EnterpriseMembershipView[]
> {
  const res =
    await apiClient.get<EnterpriseMembershipView[]>(
      "/me/enterprise-memberships",
    );
  return res.data;
}

// ---------------------------------------------------------------------------
// Tenant-admin endpoints — all require X-Enterprise-Subdomain header
// ---------------------------------------------------------------------------

function tenantHeaders(subdomain: string) {
  return { headers: { "X-Enterprise-Subdomain": subdomain } };
}

export async function fetchMyEnterprise(
  subdomain: string,
): Promise<EnterpriseFullView> {
  const res = await apiClient.get<EnterpriseFullView>(
    "/enterprise/me",
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function updateMyEnterprise(
  subdomain: string,
  payload: UpdateEnterprisePayload,
): Promise<EnterpriseFullView> {
  const res = await apiClient.patch<EnterpriseFullView>(
    "/enterprise/me",
    payload,
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function listMyEnterpriseMembers(
  subdomain: string,
): Promise<EnterpriseMember[]> {
  const res = await apiClient.get<EnterpriseMember[]>(
    "/enterprise/me/members",
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function listMyEnterpriseInvites(
  subdomain: string,
): Promise<EnterpriseInvite[]> {
  const res = await apiClient.get<EnterpriseInvite[]>(
    "/enterprise/me/invites",
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function inviteMyEnterpriseMember(
  subdomain: string,
  payload: InviteMemberPayload,
): Promise<{ inviteId: string; expiresAt: string }> {
  const res = await apiClient.post<{ inviteId: string; expiresAt: string }>(
    "/enterprise/me/invites",
    payload,
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function revokeMyEnterpriseInvite(
  subdomain: string,
  inviteId: string,
): Promise<void> {
  await apiClient.delete(
    `/enterprise/me/invites/${encodeURIComponent(inviteId)}`,
    tenantHeaders(subdomain),
  );
}

export async function revokeMyEnterpriseMember(
  subdomain: string,
  membershipId: string,
): Promise<void> {
  await apiClient.delete(
    `/enterprise/me/members/${encodeURIComponent(membershipId)}`,
    tenantHeaders(subdomain),
  );
}
